import { useState } from 'react';
import { useMatches } from 'react-router';

import { Separator } from '@/components/ui/separator';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { GlobalBanner } from '@/global/announcement/components/global-banner';
import { UserNavigation } from '@/global/navigation/components/UserNavigation';
import MainSearch from '@/global/search/components/MainSearch';
import { SidebarTrigger } from '@/global/sidebar/components/SidbarTrigger';
import { AppSidebar } from '@/global/sidebar/components/Sidebar';

import { Progress } from './progress';

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const matches = useMatches() as Array<{
    handle: { title: string; hideHeader?: boolean };
    pathname: string;
  }>;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sidebarState, _] = useState(() => {
    const state = localStorage.getItem('sidebar_state');
    // defaults to true
    return state !== 'false';
  });

  // Find the most specific route (the one with the most path segments)
  const currentRoute = matches.reduce((mostSpecific, match) => {
    if (!match.handle) return mostSpecific;
    const currentSegments = match.pathname.split('/').filter(Boolean).length;
    const mostSpecificSegments =
      mostSpecific?.pathname.split('/').filter(Boolean).length || 0;
    return currentSegments > mostSpecificSegments ? match : mostSpecific;
  }, undefined as (typeof matches)[0] | undefined);

  const pageTitle = currentRoute?.handle?.title || 'SCL';
  const hideHeader = currentRoute?.handle?.hideHeader;

  return (
    <div className="flex h-dvh overflow-hidden">
      <div className="flex w-0 flex-1 flex-col overflow-hidden">
        <SidebarProvider defaultOpen={sidebarState}>
          <AppSidebar />
          <SidebarInset>
            <GlobalBanner />
            {!hideHeader && (
              <header className="z-1 flex h-16 shrink-0 items-center gap-2 border-b border-border/40 bg-background/95 shadow-sm backdrop-blur-sm transition-[width,height] ease-linear supports-backdrop-filter:bg-background/60">
                <Progress />
                <div className="flex flex-1 items-center px-4 md:pr-6 lg:pr-8">
                  <div className="flex items-center gap-2 px-2">
                    <SidebarTrigger />
                    <Separator
                      orientation="vertical"
                      className="mr-2 hidden h-4 sm:block"
                    />
                    <h1 className="hidden whitespace-nowrap text-lg sm:block">
                      {pageTitle}
                    </h1>
                  </div>
                  <div className="ml-auto w-full px-4 sm:w-auto">
                    <MainSearch />
                  </div>
                  <div className="flex items-center">
                    <UserNavigation />
                  </div>
                </div>
              </header>
            )}
            <main className="relative flex-1 overflow-y-auto focus:outline-hidden">
              {children}
            </main>
          </SidebarInset>
        </SidebarProvider>
      </div>
    </div>
  );
}
